import React, { ReactElement, useMemo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Input } from "antd";

import * as styles from "./styles.less";
import { useProjectContext } from "../Page";
import { useUpdatePage } from "../../api/api";
import debounce from "lodash/debounce";
import { useI18n } from "../../../../../i18n";
import { ProjectKind } from "../../../../../../__generated__/globalTypes";
import { indexPageHeaders } from "../../api/pagePresentation";
import { Spinner } from "../../../../../components/Spinner";

export type A4Props = {
  pageId: string;
  children: ReactElement | ReactElement[];
  displayedTotalPageNumber: number;
  displayedPageNumber: number;
  loadMore: () => void;
  hasMoreToLoad: boolean;
  loadingMore: boolean;
  innerRef: React.Ref<any>;
};

const i18nKey = "project.a4Page";

const TopBar = React.memo(function TopBar({
  projectId,
  pageId,
  displayedPageNumber,
  displayedTotalPageNumber,
  mutePagesAfter,
  partName,
  readOnly = false,
}: {
  projectId: string;
  pageId: string;
  displayedPageNumber: number;
  displayedTotalPageNumber: number;
  mutePagesAfter: number | undefined;
  partName: string;
  readOnly: boolean;
}) {
  const [applyUpdatePage] = useUpdatePage();
  const [t] = useI18n();
  const [currentInputValue, setCurrentInputValue] = useState(partName);
  const latest = useRef({ applyUpdatePage, mutePagesAfter });
  const dirty = useRef(false);
  useLayoutEffect(() => { latest.current = { applyUpdatePage, mutePagesAfter }; }, [applyUpdatePage, mutePagesAfter]);
  const debouncedChangeHandler = useMemo(() => debounce((value: string) => {
    const { applyUpdatePage: save, mutePagesAfter: currentMutePagesAfter } = latest.current;
    if (currentMutePagesAfter === undefined) return;
    dirty.current = false;
    void save({ variables: { partname: value, pageId, projectId,
      mutePagesAfter: currentMutePagesAfter } });
  }, 1000), [pageId, projectId]);

  useEffect(() => { if (!dirty.current) setCurrentInputValue(partName); }, [partName]);
  // A mode transition must not discard the user's last keystrokes.
  useEffect(() => () => { debouncedChangeHandler.flush(); debouncedChangeHandler.cancel(); }, [debouncedChangeHandler]);

  return (
    <div className={styles.topBar} id={pageId}>
      <Input
        readOnly={readOnly}
        style={{ width: "83%" }}
        className={styles.chapterName}
        placeholder={t(`${i18nKey}.placeholder`)}
        value={currentInputValue || undefined}
        onChange={(e) => {
          const value = e.target.value;
          dirty.current = true;
          setCurrentInputValue(value);
          debouncedChangeHandler(value);
        }}
      />
      <span style={{ width: "9%" }} className={styles.totalPageNumber}>
        {displayedPageNumber}/{displayedTotalPageNumber}
      </span>
    </div>
  );
});

const Comp = ({
  children,
  displayedTotalPageNumber,
  displayedPageNumber,
  loadMore,
  hasMoreToLoad,
  loadingMore,
  pageId,
  innerRef,
}: A4Props) => {
  const { project, pages, pageHeaders } = useProjectContext();
  const headers = useMemo(() => pageHeaders || indexPageHeaders(pages), [pageHeaders, pages]);
  const header = headers.get(pageId);
  const [t] = useI18n();

  return (
    <div className={styles.root} ref={innerRef}>
      <main
        id={"page-" + displayedPageNumber}
        className={styles.contentWrapper + " pageContent"}
        style={{ display: "flex" }}
        lang={project.config.locale}>
        <TopBar
          pageId={pageId}
          mutePagesAfter={header?.page.mutePagesAfter}
          partName={header?.partName || ""}
          displayedPageNumber={displayedPageNumber}
          displayedTotalPageNumber={displayedTotalPageNumber}
          projectId={project.id}
          readOnly={project?.kind === ProjectKind.HTML_TEMPLATE}
        />
        <div>
          <div
            style={{ height: "100%", minHeight: "72em", paddingBottom: "3em" }}
            className={styles.a4Wrapper}>
            <div className={styles.content}>{children}</div>
          </div>
        </div>
        {loadingMore ? (
          <Spinner />
        ) : hasMoreToLoad ? (
          <div className={styles.showMore} onClick={loadMore}>
            {t(`${i18nKey}.showMore`)}
          </div>
        ) : null}
      </main>
    </div>
  );
};
export default React.memo(Comp);
